import {
  FileText,
  ShieldCheck,
  AlertTriangle,
  Eye,
  Plus,
  Send,
  MessageSquare,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import {
  StatCard,
  ModulePanel,
  DataTable,
  EditorialStatusBadge,
} from "@/components/admin/AdminPrimitives";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DemandesVerificationPanel, MessagesContactPanel } from "@/components/admin/InboxPanels";
import { useDemandesVerification } from "@/hooks/useDemandeVerification";
import { useMessagesContact } from "@/hooks/useMessageContact";
import { useEffect, useState } from "react";
import { ArticleDto } from "@/types/article.types";
import { VerificationDto } from "@/types/verification.types";
import { CategorieDto } from "@/types/categorie.types";
import ArticleService from "@/integrations/service/article.service";
import VerificationService from "@/integrations/service/verification.service";
import CategorieService from "@/integrations/service/categorie.service";
import { useAnalyticsSummary } from "@/hooks/useAnalytics";
import { useI18n } from "@/lib/i18n";
import { hasAnyRole } from "@/pages/admin/RequireAdmin";
import DashboardRedacteur from "@/pages/admin/DashboardRedacteur";

function DashboardPage() {
  const estRedacteurSeul = hasAnyRole(["REDACTEUR"]) && !hasAnyRole(["ADMIN", "EDITEUR"]);
  if (estRedacteurSeul) {
    return <DashboardRedacteur />;
  }
  return <DashboardAdmin />;
}

function DashboardAdmin() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<ArticleDto[]>([]);
  const [verifications, setVerifications] = useState<VerificationDto[]>([]);
  const [categories, setCategories] = useState<CategorieDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDemandes, setShowDemandes] = useState(false);
  const [showMessages, setShowMessages] = useState(false);

  const { data: demandesData } = useDemandesVerification();
  const { data: messagesData } = useMessagesContact();
  const demandesNonLues = (Array.isArray(demandesData) ? demandesData : []).filter((d: any) => !d.lu).length;
  const messagesNonLus = (Array.isArray(messagesData) ? messagesData : []).filter((m: any) => !m.lu).length;

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const articlesResponse = await ArticleService.getAll();
        const verificationsResponse = await VerificationService.getAll();
        const categoriesResponse = await CategorieService.getAll();

        setArticles(articlesResponse.content || []);
        setVerifications(verificationsResponse || []);
        setCategories(categoriesResponse || []);
        // Dans DashboardPage, après le chargement des articles
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const { data: analyticsSummary, isLoading: loadingAnalytics } = useAnalyticsSummary();
  const audience = {
    vues: analyticsSummary?.vues ?? 0,
    lectures: analyticsSummary?.lectures ?? 0,
    visiteursUniques: analyticsSummary?.visiteursUniques ?? 0,
  };

  return (
    <>
      <AdminTopbar
        title={t("admin.nav.dashboard")}
        subtitle={t("admin.dashboard.subtitle")}
      />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label={t("admin.dashboard.publishedToday")}
            value={articles.filter((a) => a.statut === "PUBLIE").length}
            hint={t("admin.dashboard.publishedToday.hint")}
            icon={FileText}
            accent="verified"
          />
          <StatCard
            label={t("admin.dashboard.pendingReview")}
            value={articles.filter((a) => a.statut !== "PUBLIE").length}
            hint={t("admin.dashboard.pendingReview.hint")}
            icon={Eye}
          />
          <StatCard
            label={t("admin.dashboard.verif")}
            value={verifications.length}
            icon={ShieldCheck}
          />
          <StatCard
            label={t("admin.dashboard.verifiedAlerts")}
            value={verifications.filter((v) => v.verdict === "FAUX").length}
            icon={AlertTriangle}
            accent="alert"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <StatCard
            label="Demandes de vérification"
            value={demandesNonLues}
            hint="non lues — cliquer pour tout voir"
            icon={Send}
            accent={demandesNonLues > 0 ? "alert" : "default"}
            onClick={() => setShowDemandes(true)}
          />
          <StatCard
            label="Messages de contact"
            value={messagesNonLus}
            hint="non lus — cliquer pour tout voir"
            icon={MessageSquare}
            accent={messagesNonLus > 0 ? "alert" : "default"}
            onClick={() => setShowMessages(true)}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <ModulePanel
              title={t("admin.dashboard.recentActivity")}
              description={t("admin.dashboard.recentActivity.desc")}
              action={
                <Link
                  to="/redaction/articles/nouveau"
                  className="inline-flex items-center gap-1.5 rounded-md bg-foreground px-3 py-1.5 text-xs font-semibold text-background hover:opacity-90 transition-opacity"
                >
                  <Plus className="h-3.5 w-3.5" /> {t("admin.article.new")}
                </Link>
              }
            >
              <DataTable
                columns={[
                  t("admin.common.col.title"),
                  t("admin.common.col.type"),
                  t("admin.common.col.status"),
                  t("admin.common.col.updated"),
                ]}
                rows={articles
                  .slice(0, 6)
                  .map((a) => [
                    <button
                      onClick={() => navigate(`/redaction/articles/${a.id}`)}
                      className="text-left font-medium text-foreground line-clamp-1 hover:underline"
                    >
                      {a.traductions?.[0]?.titre}
                    </button>,
                    a.type,
                    <EditorialStatusBadge status={a.statut} />,
                    new Date(a.modifieLe || a.publieLe).toLocaleString("fr-FR"),
                  ])}
              />
            </ModulePanel>
          </div>
          <div className="space-y-6">
            <ModulePanel
              title={t("admin.dashboard.audience")}
              description={t("admin.dashboard.audience.desc")}
            >
              <div className="grid grid-cols-3 gap-2 sm:gap-3 text-center">
                <div>
                  <div className="font-serif text-xl font-semibold">
                    {loadingAnalytics ? "—" : audience.vues.toLocaleString("fr-FR")}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("admin.dashboard.audience.views")}
                  </div>
                </div>
                <div>
                  <div className="font-serif text-xl font-semibold">
                    {loadingAnalytics ? "—" : audience.lectures.toLocaleString("fr-FR")}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("admin.dashboard.audience.reads")}
                  </div>
                </div>
                <div>
                  <div className="font-serif text-xl font-semibold">
                    {loadingAnalytics ? "—" : audience.visiteursUniques.toLocaleString("fr-FR")}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("admin.dashboard.audience.visitors")}
                  </div>
                </div>
              </div>
            </ModulePanel>
          </div>
        </div>

        <ModulePanel title={t("admin.dashboard.pendingVerif")}>
          <DataTable
            columns={[
              t("admin.common.col.claim"),
              t("admin.dashboard.col.proposedVerdict"),
              t("admin.dashboard.col.validatedBy"),
              t("admin.common.col.date"),
            ]}
            rows={verifications
              .slice(0, 4)
              .map((f) => [
                <button
                  onClick={() => navigate(`/redaction/verifications/${f.id}`)}
                  className="line-clamp-1 text-left text-foreground font-medium hover:underline"
                >
                  «{" "}
                  {f.traductions?.[0]?.affirmation ||
                    t("admin.dashboard.noTranslation")}{" "}
                  »
                </button>,
                <EditorialStatusBadge
                  status={f.verdict === "VRAI" ? "Publié" : "En vérification"}
                />,
                "—",
                new Date(f.date).toLocaleDateString("fr-FR"),
              ])}
          />
        </ModulePanel>
      </div>

      <Dialog open={showDemandes} onOpenChange={setShowDemandes}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DemandesVerificationPanel />
        </DialogContent>
      </Dialog>

      <Dialog open={showMessages} onOpenChange={setShowMessages}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <MessagesContactPanel />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default DashboardPage;
